// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Formatting helpers. All display numbers flow through here. */

export function formatNumber(n: number | null | undefined, dp = 0): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function formatPercent(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(dp)}%`;
}

export function formatCompact(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${(n / 1e6).toFixed(abs >= 1e7 ? 1 : 2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}k`;
  return String(Math.round(n));
}

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Format an incident's date parts as loosely as the data allows. */
export function formatIncidentDate(y: number | null, m: number | null, d: number | null): string {
  if (y == null) return 'Date unknown';
  if (m == null) return String(y);
  const mon = MONTHS_FULL[m - 1] ?? '';
  if (d == null) return `${mon} ${y}`;
  return `${d} ${mon} ${y}`;
}

/** minutes-of-day → "2:30 pm". */
export function formatTime(mins: number | null): string {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60);
  const mi = mins % 60;
  const ampm = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mi).padStart(2, '0')} ${ampm}`;
}

export function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

/** Title-case a lower-cased location string ("byron bay beach" → "Byron Bay Beach"). */
export function titleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/\b(Nsw|Qld|Wa|Sa|Vic|Tas|Nt|Act)\b/gi, (m) => m.toUpperCase());
}
